// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React from "react";
import { X } from "lucide-react";
import SpaceUseCaseConfig from "./SpaceUseCaseConfig";

const SpacePropertiesModal = ({
  isOpen,
  onClose,
  mode,
  element,
  updateSpaceMetadata,
  updateSingleSpace,
  createSingleSpace,
  deleteSingleSpace,
  savingSingleSpace,
  deletingSingleSpace,
  useCases,
}) => {
  if (!isOpen || !element) return null;

  const sel = element;
  const set = (patch) => updateSpaceMetadata(sel.id, patch);

  const fieldClass =
    "w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent";
  const labelClass =
    "block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1";

  // Ensure space_type is always populated (select shows "event_area" as
  // default but the underlying value may still be empty for freshly-drawn
  // shapes). Patch it once so downstream validation and save see a value.
  if (!sel.space_type) {
    updateSpaceMetadata(sel.id, { space_type: "event_area" });
  }

  const missing = [];
  if (!(sel.space_code || "").trim()) missing.push("Code");
  if (!(sel.name || sel.label || "").trim()) missing.push("Name");

  const handleSave = async () => {
    if (sel.spaceId) {
      await updateSingleSpace(sel.id);
    } else {
      await createSingleSpace(sel.id);
    }
    onClose();
  };

  const handleDelete = () => {
    deleteSingleSpace(sel.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col border border-gray-200 dark:border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
              {mode === "add" ? "Neue Fläche erstellen" : "Fläche bearbeiten"}
            </h2>
            {mode === "edit" && sel.spaceId ? (
              <span className="text-xs text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded">
                ID: {sel.spaceId}
              </span>
            ) : (
              <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded">
                Neu (noch nicht gespeichert)
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {/* Validation banner */}
          {missing.length > 0 && (
            <div className="mb-4 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200">
              Pflichtfelder fehlen: <strong>{missing.join(", ")}</strong>
            </div>
          )}

          {/* Identifikation */}
          <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-2 font-semibold">
            Identifikation
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <div>
              <label className={labelClass}>
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                maxLength={50}
                value={sel.space_code || ""}
                onChange={(e) => set({ space_code: e.target.value })}
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Nummer</label>
              <input
                type="text"
                value={sel.space_number ?? ""}
                onChange={(e) => set({ space_number: e.target.value })}
                className={fieldClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Beschreibung</label>
              <textarea
                rows={2}
                value={sel.description || ""}
                onChange={(e) => set({ description: e.target.value })}
                className={fieldClass}
              />
            </div>
          </div>

          {/* Typ & Kapazität */}
          <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-2 font-semibold">
            Typ &amp; Kapazität
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
            <div>
              <label className={labelClass}>Typ</label>
              <select
                value={sel.space_type || "event_area"}
                onChange={(e) => set({ space_type: e.target.value })}
                className={fieldClass}
              >
                <option value="event_area">event_area</option>
                <option value="parking_spot">parking_spot</option>
                <option value="delivery_zone">delivery_zone</option>
                <option value="radbox_slot">radbox_slot</option>
                <option value="charging_point">charging_point</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Kapazität</label>
              <input
                type="number"
                min="0"
                value={sel.capacity ?? ""}
                onChange={(e) =>
                  set({
                    capacity:
                      e.target.value === ""
                        ? null
                        : parseInt(e.target.value, 10),
                  })
                }
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Einheit</label>
              <input
                type="text"
                maxLength={50}
                value={sel.capacity_unit || "persons"}
                onChange={(e) => set({ capacity_unit: e.target.value })}
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Wartung</label>
              <select
                value={sel.maintenance_status || "operational"}
                onChange={(e) => set({ maintenance_status: e.target.value })}
                className={fieldClass}
              >
                <option value="operational">operational</option>
                <option value="maintenance">maintenance</option>
                <option value="out_of_service">out_of_service</option>
              </select>
            </div>
          </div>

          {/* Maße */}
          <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-2 font-semibold">
            Maße (auto aus Form, überschreibbar)
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            <div>
              <label className={labelClass}>Länge (m)</label>
              <input
                type="number"
                step="0.01"
                value={sel.length_m ?? ""}
                onChange={(e) =>
                  set({
                    length_m:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                placeholder="auto"
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Breite (m)</label>
              <input
                type="number"
                step="0.01"
                value={sel.width_m ?? ""}
                onChange={(e) =>
                  set({
                    width_m:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                placeholder="auto"
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Höhe (m)</label>
              <input
                type="number"
                step="0.01"
                value={sel.height_m ?? ""}
                onChange={(e) =>
                  set({
                    height_m:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Fläche (m²)</label>
              <input
                type="number"
                step="0.01"
                value={sel.area_sqm ?? ""}
                onChange={(e) =>
                  set({
                    area_sqm:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                placeholder="auto"
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Ausrichtung (°)</label>
              <input
                type="number"
                value={sel.orientation ?? ""}
                onChange={(e) =>
                  set({
                    orientation:
                      e.target.value === ""
                        ? null
                        : parseInt(e.target.value, 10),
                  })
                }
                placeholder="auto"
                className={fieldClass}
              />
            </div>
          </div>

          {/* GPS */}
          <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-2 font-semibold">
            GPS
          </div>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div>
              <label className={labelClass}>Lat</label>
              <input
                type="number"
                step="0.00000001"
                value={sel.gps_lat ?? ""}
                onChange={(e) =>
                  set({
                    gps_lat:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Lng</label>
              <input
                type="number"
                step="0.00000001"
                value={sel.gps_lng ?? ""}
                onChange={(e) =>
                  set({
                    gps_lng:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                className={fieldClass}
              />
            </div>
          </div>

          {/* Ausstattung / Amenities */}
          <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-2 font-semibold">
            Ausstattung
          </div>
          <div className="mb-5">
            <div className="flex flex-wrap items-center gap-1.5 px-2.5 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 rounded min-h-[38px]">
              {(Array.isArray(sel.amenities) ? sel.amenities : []).map(
                (am, i) => (
                  <span
                    key={`${am}-${i}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-primary-color-tint primary-color"
                  >
                    {am}
                    <button
                      type="button"
                      onClick={() =>
                        set({
                          amenities: sel.amenities.filter((_, j) => j !== i),
                        })
                      }
                      className="hover:opacity-70"
                      title="Entfernen"
                    >
                      ×
                    </button>
                  </span>
                ),
              )}
              <input
                type="text"
                placeholder={
                  (sel.amenities || []).length === 0
                    ? "z.B. Strom, WLAN, WC — Enter zum Hinzufügen"
                    : ""
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    const v = e.currentTarget.value.trim();
                    if (v && !(sel.amenities || []).includes(v)) {
                      set({ amenities: [...(sel.amenities || []), v] });
                    }
                    e.currentTarget.value = "";
                  }
                }}
                onBlur={(e) => {
                  const v = e.currentTarget.value.trim();
                  if (v && !(sel.amenities || []).includes(v)) {
                    set({ amenities: [...(sel.amenities || []), v] });
                  }
                  e.currentTarget.value = "";
                }}
                className="flex-1 min-w-[140px] bg-transparent text-sm outline-none dark:text-slate-200 dark:placeholder-slate-400"
              />
            </div>
          </div>

          {/* Zugangskontrolle */}
          <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-2 font-semibold">
            Zugang
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <div>
              <label className={labelClass}>Zugangstyp</label>
              <select
                value={sel.access_control_type || ""}
                onChange={(e) =>
                  set({ access_control_type: e.target.value || null })
                }
                className={fieldClass}
              >
                <option value="">— kein —</option>
                <option value="barrier">barrier</option>
                <option value="bollard">bollard</option>
                <option value="lock">lock</option>
                <option value="none">none</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Geräte-ID</label>
              <input
                type="text"
                maxLength={100}
                value={sel.access_control_device_id || ""}
                onChange={(e) =>
                  set({ access_control_device_id: e.target.value })
                }
                className={fieldClass}
              />
            </div>
          </div>

          {/* Status */}
          <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-2 font-semibold">
            Status
          </div>
          <div className="flex items-center gap-6 mb-5">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={sel.is_active !== false}
                onChange={(e) => set({ is_active: e.target.checked })}
                className="rounded"
              />
              Aktiv
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={sel.is_bookable !== false}
                onChange={(e) => set({ is_bookable: e.target.checked })}
                className="rounded"
              />
              Buchbar
            </label>
          </div>

          {/* Nutzungsart-Konfiguration */}
          <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
            <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-2 font-semibold">
              Nutzungsart-Konfiguration
            </div>
            <SpaceUseCaseConfig spaceId={sel.spaceId} useCases={useCases} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 shrink-0 rounded-b-xl">
          <div>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deletingSingleSpace}
              className="text-sm px-4 py-2 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800 disabled:opacity-50"
            >
              {deletingSingleSpace ? "Löscht…" : "Löschen"}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-4 py-2 rounded-lg text-gray-700 dark:text-slate-200 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 border border-gray-300 dark:border-slate-600"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={savingSingleSpace || missing.length > 0}
              className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingSingleSpace
                ? "Speichert…"
                : sel.spaceId
                  ? "Aktualisieren"
                  : "Speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpacePropertiesModal;
